import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ShieldIcon from '@mui/icons-material/Shield'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import defaultEmployeeTeams from '../../../../helpers/auth/_defaultEmployeeTeams'

// Site Config → Employees → Manage Access. One row per active employee, one
// column per management team plus PTS (co-ordinator) and Control (CAD level).
// Site admins are pinned to the top with everything granted: they bypass
// every team check on the server, so showing them unticked would be a lie.

const CAD_LEVELS = ['Viewer', 'Call Taker', 'Dispatcher', 'Commander']

const headCellSx = {
  whiteSpace: 'nowrap',
  fontWeight: 700,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'white',
  textAlign: 'center',
  px: 1,
}

const ManageAccess = () => {
  const { setToast } = useContext(GlobalContext)
  const { user: currentUser } = useContext(UserContext)
  const [loading, setLoading] = useState(true)
  const [employees, setEmployees] = useState([])
  const [additionalTeams, setAdditionalTeams] = useState([])
  const [search, setSearch] = useState('')
  const [savingId, setSavingId] = useState(null)

  const load = () => {
    setLoading(true)
    API.get('/manage-access')
      .then((res) => {
        if (res.status === 200) {
          setEmployees(res.data.employees ?? [])
          setAdditionalTeams(res.data.additionalTeams ?? [])
        } else {
          setToast({
            message: res.data?.error || 'Unable to load access list',
            severity: 'error',
          })
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const teamColumns = useMemo(
    () => [...defaultEmployeeTeams, ...additionalTeams],
    [additionalTeams],
  )

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return employees
    return employees.filter((e) =>
      `${e.firstName} ${e.lastName} ${e.email ?? ''} ${e.clinicalRole ?? ''}`
        .toLowerCase()
        .includes(q),
    )
  }, [employees, search])

  const isSelf = (row) => String(row.userID) === String(currentUser?._id)
  const canEditRow = (row) =>
    !row.isSiteAdmin && (currentUser?.security?.isSiteAdmin || !isSelf(row))

  const applyChange = (row, patch) => {
    // Optimistic — the grid is the whole point of this page, waiting on a
    // round trip per tick would feel broken. Roll back on failure.
    const previous = row
    setEmployees((prev) =>
      prev.map((e) => (e._id === row._id ? { ...e, ...patch } : e)),
    )
    setSavingId(row._id)
    API.put(`/manage-access/${row._id}`, patch)
      .then((res) => {
        if (res.status === 200) {
          setEmployees((prev) =>
            prev.map((e) => (e._id === row._id ? { ...e, ...res.data } : e)),
          )
        } else {
          setEmployees((prev) =>
            prev.map((e) => (e._id === row._id ? previous : e)),
          )
          setToast({
            message: res.data?.error || 'Unable to update access',
            severity: 'error',
          })
        }
      })
      .finally(() => setSavingId(null))
  }

  const toggleTeam = (row, team) => {
    const has = row.teams.includes(team)
    applyChange(row, {
      teams: has ? row.teams.filter((t) => t !== team) : [...row.teams, team],
    })
  }

  return (
    <Stack gap={2}>
      <Stack>
        <Typography variant="title2" fontWeight={700}>
          Manage Access
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Who belongs to which management team, who can co-ordinate patient
          transport, and who can use the Control room. Changes save as you make
          them.
        </Typography>
      </Stack>

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Site administrators have every permission automatically and are shown at
        the top. Add or remove site administrators under Organisation → Site
        Admins. Teams are for management duties — frontline staff do not need
        any of these to book shifts, complete records or report incidents.
      </Alert>

      <TextField
        size="small"
        placeholder="Search staff"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 360 }}
      />

      {loading && <LinearProgress />}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headCellSx,
                  textAlign: 'left',
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  bgcolor: 'background.paper',
                  minWidth: 220,
                }}
              >
                Staff member
              </TableCell>
              {teamColumns.map((team) => (
                <TableCell key={team} sx={headCellSx}>
                  {team}
                </TableCell>
              ))}
              <TableCell
                sx={{
                  ...headCellSx,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Tooltip title="Patient Transport co-ordinator: bookings, allocation and patient lookup">
                  <span>PTS</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 150 }}>
                <Tooltip title="Command & Control room access level">
                  <span>Control</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const editable = canEditRow(row)
              const busy = savingId === row._id
              return (
                <TableRow
                  key={row._id}
                  hover
                  sx={{
                    bgcolor: row.isSiteAdmin ? 'action.hover' : undefined,
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      bgcolor: row.isSiteAdmin ? '#f5f5f5' : 'background.paper',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Avatar
                        src={row.photograph}
                        sx={{ width: 32, height: 32 }}
                      />
                      <Box minWidth={0}>
                        <Typography fontWeight={600} noWrap>
                          {row.firstName} {row.lastName}
                        </Typography>
                        {row.isSiteAdmin ? (
                          <Chip
                            size="small"
                            icon={<ShieldIcon />}
                            label={
                              row.isTenantOwner
                                ? 'Site Admin · Owner'
                                : 'Site Admin'
                            }
                            color="anPurple"
                            variant="outlined"
                            sx={{
                              borderRadius: 1,
                              height: 20,
                              fontSize: '0.68rem',
                              mt: 0.25,
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            {row.clinicalRole || row.email}
                            {isSelf(row) && !currentUser?.security?.isSiteAdmin
                              ? ' · you'
                              : ''}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>

                  {teamColumns.map((team) => {
                    const checked = row.isSiteAdmin || row.teams.includes(team)
                    return (
                      <TableCell key={team} align="center" sx={{ px: 0.5 }}>
                        <Tooltip
                          title={
                            row.isSiteAdmin
                              ? 'Site admins have every permission'
                              : !editable
                                ? 'You cannot change your own access'
                                : ''
                          }
                        >
                          <span>
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={!editable || busy}
                              onChange={() => toggleTeam(row, team)}
                              inputProps={{
                                'aria-label': `${team} for ${row.firstName} ${row.lastName}`,
                              }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    )
                  })}

                  <TableCell
                    align="center"
                    sx={{
                      px: 0.5,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Tooltip
                      title={
                        row.isSiteAdmin
                          ? 'Site admins have every permission'
                          : ''
                      }
                    >
                      <span>
                        <Checkbox
                          size="small"
                          checked={row.isSiteAdmin || row.ptsCoordinator}
                          disabled={!editable || busy}
                          onChange={(e) =>
                            applyChange(row, {
                              ptsCoordinator: e.target.checked,
                            })
                          }
                          inputProps={{
                            'aria-label': `PTS co-ordinator for ${row.firstName} ${row.lastName}`,
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center" sx={{ px: 1 }}>
                    {row.isSiteAdmin ? (
                      <Chip
                        size="small"
                        label="Commander"
                        color="anPurple"
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    ) : (
                      <Select
                        size="small"
                        value={row.cadAccess || ''}
                        displayEmpty
                        disabled={!editable || busy}
                        onChange={(e) =>
                          applyChange(row, {
                            cadAccess: e.target.value || null,
                          })
                        }
                        sx={{ minWidth: 130, fontSize: '0.85rem' }}
                        inputProps={{
                          'aria-label': `Control access for ${row.firstName} ${row.lastName}`,
                        }}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {CAD_LEVELS.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={teamColumns.length + 3}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={2}
                  >
                    No staff match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary">
        Only Live and New Starter employees are listed. Teams beyond the
        standard set come from Employees → Staff → Additional Teams.
      </Typography>
    </Stack>
  )
}

export default ManageAccess
